import { showError } from 'legacy/common/utils/toast';
import {
  useCreateEventWaiver,
  useUpdateEventWaiver,
  useDeleteEventWaiver
} from 'legacy/events/hooks';

interface Waiver {
  content: string;
  title: string;
  id: string;
}
const useWaiverChangeHandler = (eventId: string) => {
  const {
    mutateAsync: createEventWaiver,
    isLoading: createEventWaiverLoading
  } = useCreateEventWaiver(eventId);
  const {
    mutateAsync: updateEventWaiver,
    isLoading: updateEventWaiverLoading
  } = useUpdateEventWaiver(eventId);
  const {
    mutateAsync: deleteEventWaiver,
    isLoading: deleteEventWaiverLoading
  } = useDeleteEventWaiver(eventId);

  const handleWaiversChange = async (
    originalWaivers: Waiver[],
    currentWaivers: Waiver[]
  ) => {
    const originalMap = new Map(
      originalWaivers.map((waiver) => [waiver.id, waiver])
    );
    const currentMap = new Map(
      currentWaivers.map((waiver) => [waiver.id, waiver])
    );

    const created: Waiver[] = [];
    const updated: Waiver[] = [];
    const deleted: Waiver[] = [];

    const isPlaceholderWaiver = (waiver: Waiver) =>
      waiver.id === '' && waiver.title.trim() === '';
    const isWaiverCreated = (waiver: Waiver) =>
      !originalMap.has(waiver.id) || waiver.id === '';
    const isWaiverUpdated = (waiver: Waiver) =>
      JSON.stringify(originalMap.get(waiver.id)) !== JSON.stringify(waiver);

    // Determine new and updated waivers
    currentWaivers.forEach((waiver) => {
      if (isPlaceholderWaiver(waiver)) {
        // eslint-disable-next-line no-empty
      } else if (isWaiverCreated(waiver)) {
        created.push(waiver);
      } else if (isWaiverUpdated(waiver)) {
        updated.push(waiver);
      }
    });

    // Determine deleted waivers
    originalWaivers.forEach((waiver) => {
      if (!currentMap.has(waiver.id)) {
        deleted.push(waiver);
      }
    });

    try {
      await Promise.all([
        // API calls for created waivers
        ...created.map(({ id: _, ...waiver }) => createEventWaiver(waiver)),
        // API calls for updated waivers
        ...updated.map(({ id, ...waiver }) =>
          updateEventWaiver({
            waiverId: id,
            waiverData: waiver
          })
        ),
        // API calls for deleted waivers
        ...deleted.map((waiver) => deleteEventWaiver({ waiverId: waiver.id }))
      ]);
    } catch (error) {
      showError({
        title: 'Error saving waiver',
        message: error?.message
      });
    }
  };

  return {
    handleWaiversChange,
    createEventWaiverLoading,
    updateEventWaiverLoading,
    deleteEventWaiverLoading
  };
};

export default useWaiverChangeHandler;
